import { useEffect, useMemo, useRef, useState } from 'react';
import type { Module, PortalContent, Prompt } from './types';
import { IconArrowRight, IconCheck, IconTarget } from './icons';
import { loadResult, STORAGE_KEY, type PriorityFinderResult } from './priority-finder-data';

/**
 * "Your next move" block. Renders in two modes:
 *
 *   Priority Finder mode (BCE): follows the 12-prompt implementation_order
 *   generated by the assessment.
 *
 *   Module sequence mode (OME): guides the buyer through Part 1 (foundation)
 *   first, then lets them pick a platform, then walks the prompts in that
 *   platform, then returns to the picker for the next platform. Repeats until
 *   all 20 prompts are copied.
 *
 * The block is sticky to the top of the viewport once scrolled past, so the
 * "next move" stays visible no matter how deep the user is in the prompt list.
 */

interface Props {
  content: PortalContent;
  copiedPromptIds: Set<string>;
  /** Priority Finder bonus ID (BCE only). Null when not applicable. */
  bonusId: string | null;
  /** Opens the Priority Finder modal (BCE only). Undefined when not applicable. */
  onViewFullResults?: () => void;
}

type ModeState =
  | { kind: 'priority_finder'; result: PriorityFinderResult }
  | { kind: 'module_sequence' }
  | null;

type SequenceState =
  | { type: 'foundation_pending'; nextPromptId: string; copiedInFoundation: number; totalFoundation: number; foundation: Module }
  | { type: 'platform_picker'; remaining: Module[]; totalCopied: number; grandTotal: number }
  | { type: 'platform_active'; module: Module; nextPromptId: string; copiedInModule: number; totalInModule: number; totalCopied: number; grandTotal: number }
  | { type: 'all_complete'; grandTotal: number };

type PFState =
  | { type: 'next'; promptId: string; index: number; total: number; copiedCount: number }
  | { type: 'all_complete'; total: number };

export function NextUpBlock({ content, copiedPromptIds, bonusId, onViewFullResults }: Props) {
  const mode = useModeState(content, bonusId);
  const [isStuck, setIsStuck] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) setIsStuck(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '0px 0px 0px 0px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const promptLookup = useMemo(() => {
    const map = new Map<string, Prompt>();
    for (const p of content.prompts) map.set(p.prompt_id, p);
    return map;
  }, [content.prompts]);

  if (!mode) return null;

  return (
    <>
      <div ref={sentinelRef} className="next-up-sentinel" aria-hidden="true" />
      <section className={`next-up${isStuck ? ' is-stuck' : ''}`}>
        {mode.kind === 'priority_finder' ? (
          <PriorityFinderBody
            result={mode.result}
            copiedPromptIds={copiedPromptIds}
            promptLookup={promptLookup}
            isStuck={isStuck}
            onViewFullResults={onViewFullResults}
          />
        ) : (
          <ModuleSequenceBody
            content={content}
            copiedPromptIds={copiedPromptIds}
            promptLookup={promptLookup}
            isStuck={isStuck}
          />
        )}
      </section>
    </>
  );
}

function useModeState(content: PortalContent, bonusId: string | null): ModeState {
  const [result, setResult] = useState<PriorityFinderResult | null>(() => {
    if (!bonusId || typeof window === 'undefined') return null;
    return loadResult(content.product_id, bonusId);
  });

  useEffect(() => {
    if (!bonusId) return;
    const key = STORAGE_KEY(content.product_id, bonusId);
    function refresh() {
      setResult(loadResult(content.product_id, bonusId!));
    }
    function onStorage(e: StorageEvent) {
      if (e.key === key) refresh();
    }
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', refresh);
    };
  }, [bonusId, content.product_id]);

  // Keep in sync with copy events (parent re-renders when copiedPromptIds changes).
  useEffect(() => {
    if (!bonusId) return;
    setResult(loadResult(content.product_id, bonusId));
  }, [bonusId, content.product_id]);

  if (bonusId && result) return { kind: 'priority_finder', result };

  const hasFoundation = content.modules?.some((m) => m.is_foundation) ?? false;
  if (hasFoundation) return { kind: 'module_sequence' };

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Priority Finder body (BCE)
// ─────────────────────────────────────────────────────────────────────────────

function PriorityFinderBody({
  result,
  copiedPromptIds,
  promptLookup,
  isStuck,
  onViewFullResults,
}: {
  result: PriorityFinderResult;
  copiedPromptIds: Set<string>;
  promptLookup: Map<string, Prompt>;
  isStuck: boolean;
  onViewFullResults?: () => void;
}) {
  const state = useMemo<PFState>(() => {
    const total = result.implementation_order.length;
    const nextId = result.implementation_order.find((id) => !copiedPromptIds.has(id));
    const copiedCount = result.implementation_order.filter((id) => copiedPromptIds.has(id)).length;
    if (!nextId) return { type: 'all_complete', total };
    const index = result.implementation_order.indexOf(nextId);
    return { type: 'next', promptId: nextId, index, total, copiedCount };
  }, [result, copiedPromptIds]);

  const copiedCount = state.type === 'all_complete' ? state.total : state.copiedCount;
  const total = state.total;
  const progressPct = total === 0 ? 0 : Math.round((copiedCount / total) * 100);

  return (
    <>
      <div className="next-up-head">
        <div className="next-up-label">
          <IconTarget className="next-up-icon" aria-hidden="true" />
          <span>Priority Finder · Your next move</span>
        </div>
        <div className="next-up-progress">
          <strong>{copiedCount}</strong> of {total} copied
        </div>
      </div>

      <div className="next-up-bar">
        <div className="next-up-bar-fill" style={{ width: `${progressPct}%` }} />
      </div>

      {state.type === 'all_complete' ? (
        <div className="next-up-body">
          <div className="next-up-done">
            <IconCheck className="next-up-done-icon" aria-hidden="true" />
            <div>
              <div className="next-up-done-title">You've copied all {total} systems.</div>
              <div className="next-up-done-sub">
                Retake the Priority Finder to see how your scores have moved.
              </div>
            </div>
          </div>
          <button type="button" className="next-up-cta" onClick={onViewFullResults}>
            Retake
            <IconArrowRight aria-hidden="true" />
          </button>
        </div>
      ) : (
        <NextPromptBody
          promptLookup={promptLookup}
          promptId={state.promptId}
          rankLabel={`Next · ${String(state.index + 1).padStart(2, '0')} of ${total}`}
          isStuck={isStuck}
          primaryCtaLabel="Take me there"
          onPrimaryCta={() => scrollToPrompt(state.promptId)}
          secondaryLink={
            onViewFullResults
              ? { label: 'View full results', onClick: onViewFullResults }
              : undefined
          }
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Module sequence body (OME)
// ─────────────────────────────────────────────────────────────────────────────

function ModuleSequenceBody({
  content,
  copiedPromptIds,
  promptLookup,
  isStuck,
}: {
  content: PortalContent;
  copiedPromptIds: Set<string>;
  promptLookup: Map<string, Prompt>;
  isStuck: boolean;
}) {
  const [activePlatformId, setActivePlatformId] = useState<string | null>(null);

  // Auto-clear the stored pick once that platform is fully copied.
  useEffect(() => {
    if (!activePlatformId) return;
    const m = content.modules?.find((mod) => mod.module_id === activePlatformId);
    if (m && m.prompt_ids.every((id) => copiedPromptIds.has(id))) {
      setActivePlatformId(null);
    }
  }, [activePlatformId, copiedPromptIds, content.modules]);

  const state = useMemo<SequenceState>(
    () => deriveSequenceState(content, copiedPromptIds, activePlatformId),
    [content, copiedPromptIds, activePlatformId],
  );

  const grandTotal = state.type === 'foundation_pending' ? content.prompts.length : state.grandTotal;
  const totalCopied =
    state.type === 'foundation_pending'
      ? copiedPromptIds.size
      : state.type === 'platform_picker' || state.type === 'platform_active'
        ? state.totalCopied
        : state.grandTotal;
  const progressPct = grandTotal === 0 ? 0 : Math.round((totalCopied / grandTotal) * 100);

  return (
    <>
      <div className="next-up-head">
        <div className="next-up-label">
          <IconTarget className="next-up-icon" aria-hidden="true" />
          <span>One-Message Engine · Your next move</span>
        </div>
        <div className="next-up-progress">
          <strong>{totalCopied}</strong> of {grandTotal} copied
        </div>
      </div>

      <div className="next-up-bar">
        <div className="next-up-bar-fill" style={{ width: `${progressPct}%` }} />
      </div>

      {state.type === 'foundation_pending' ? (
        <NextPromptBody
          promptLookup={promptLookup}
          promptId={state.nextPromptId}
          rankLabel={`Core Message · ${state.copiedInFoundation} of ${state.totalFoundation}`}
          isStuck={isStuck}
          primaryCtaLabel="Take me there"
          onPrimaryCta={() => scrollToPrompt(state.nextPromptId)}
        />
      ) : state.type === 'platform_active' ? (
        <NextPromptBody
          promptLookup={promptLookup}
          promptId={state.nextPromptId}
          rankLabel={`${cleanModuleTitle(state.module)} · ${state.copiedInModule} of ${state.totalInModule}`}
          isStuck={isStuck}
          primaryCtaLabel="Take me there"
          onPrimaryCta={() => scrollToPrompt(state.nextPromptId)}
          secondaryLink={
            state.copiedInModule === 0
              ? {
                  label: 'Pick a different platform',
                  onClick: () => {
                    setActivePlatformId(null);
                    const sentinel = document.querySelector<HTMLElement>('.next-up-sentinel');
                    if (!sentinel) return;
                    const absoluteY = window.scrollY + sentinel.getBoundingClientRect().top;
                    window.scrollTo({ top: absoluteY, behavior: 'smooth' });
                  },
                }
              : undefined
          }
        />
      ) : state.type === 'platform_picker' ? (
        <PlatformPickerBody
          platforms={state.remaining}
          isStuck={isStuck}
          onPick={setActivePlatformId}
        />
      ) : (
        <div className="next-up-body">
          <div className="next-up-done">
            <IconCheck className="next-up-done-icon" aria-hidden="true" />
            <div>
              <div className="next-up-done-title">You've copied all {state.grandTotal} prompts.</div>
              <div className="next-up-done-sub">
                Run the Alignment Audit bonus to check everything ties together.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function deriveSequenceState(
  content: PortalContent,
  copied: Set<string>,
  activePlatformId: string | null,
): SequenceState {
  const modules = content.modules ?? [];
  const foundation = modules.find((m) => m.is_foundation);
  if (!foundation) {
    return { type: 'all_complete', grandTotal: content.prompts.length };
  }

  const grandTotal = content.prompts.length;
  const totalCopied = content.prompts.filter((p) => copied.has(p.prompt_id)).length;

  // Step 1: foundation not yet complete → direct to next foundation prompt.
  const foundationCopied = foundation.prompt_ids.filter((id) => copied.has(id)).length;
  if (foundationCopied < foundation.prompt_ids.length) {
    const nextId = foundation.prompt_ids.find((id) => !copied.has(id));
    if (!nextId) {
      return { type: 'all_complete', grandTotal };
    }
    return {
      type: 'foundation_pending',
      nextPromptId: nextId,
      copiedInFoundation: foundationCopied,
      totalFoundation: foundation.prompt_ids.length,
      foundation,
    };
  }

  // Step 2: foundation complete. Look at platform (non-foundation) modules.
  const platforms = modules.filter((m) => !m.is_foundation);

  // Any platform with partial progress takes priority (in-flight work).
  const inProgress = platforms.find((m) => {
    const c = m.prompt_ids.filter((id) => copied.has(id)).length;
    return c > 0 && c < m.prompt_ids.length;
  });

  if (inProgress) {
    const nextId = inProgress.prompt_ids.find((id) => !copied.has(id))!;
    const copiedInModule = inProgress.prompt_ids.filter((id) => copied.has(id)).length;
    return {
      type: 'platform_active',
      module: inProgress,
      nextPromptId: nextId,
      copiedInModule,
      totalInModule: inProgress.prompt_ids.length,
      totalCopied,
      grandTotal,
    };
  }

  // Next: honour the user's explicit pick (clicked a platform card but hasn't
  // copied anything yet).
  if (activePlatformId) {
    const picked = platforms.find((m) => m.module_id === activePlatformId);
    if (picked && picked.prompt_ids.some((id) => !copied.has(id))) {
      const nextId = picked.prompt_ids.find((id) => !copied.has(id))!;
      return {
        type: 'platform_active',
        module: picked,
        nextPromptId: nextId,
        copiedInModule: picked.prompt_ids.filter((id) => copied.has(id)).length,
        totalInModule: picked.prompt_ids.length,
        totalCopied,
        grandTotal,
      };
    }
  }

  // Step 3: no in-progress platform, no explicit pick. Show remaining platforms.
  const remaining = platforms.filter((m) =>
    m.prompt_ids.some((id) => !copied.has(id)),
  );

  if (remaining.length === 0) {
    return { type: 'all_complete', grandTotal };
  }

  return { type: 'platform_picker', remaining, totalCopied, grandTotal };
}

function cleanModuleTitle(m: Module): string {
  // Strip "Part N — " prefix for display inside the next-up block.
  return m.title.replace(/^Part\s+\d+\s*[—-]\s*/, '');
}

function PlatformPickerBody({
  platforms,
  isStuck,
  onPick,
}: {
  platforms: Module[];
  isStuck: boolean;
  onPick: (moduleId: string) => void;
}) {
  if (isStuck) {
    // Compact mode: show a single-line summary with "Pick a platform" cta.
    return (
      <div className="next-up-body next-up-body-compact">
        <div className="next-up-next">
          <div className="next-up-rank">Core Message complete</div>
          <div className="next-up-title">Pick a platform to tackle next</div>
        </div>
        <div className="next-up-actions">
          <button
            type="button"
            className="next-up-cta"
            onClick={() => {
              const sentinel = document.querySelector<HTMLElement>('.next-up-sentinel');
              if (!sentinel) return;
              const absoluteY =
                window.scrollY + sentinel.getBoundingClientRect().top;
              window.scrollTo({ top: absoluteY, behavior: 'smooth' });
            }}
          >
            Choose
            <IconArrowRight aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  function pickPlatform(moduleId: string) {
    onPick(moduleId);
    const module_ = platforms.find((p) => p.module_id === moduleId);
    const firstPromptId = module_?.prompt_ids[0];
    // The picker collapses into a shorter active-state banner on re-render,
    // which shifts the page up. Wait for React commit + browser layout before
    // computing the scroll target, otherwise we anchor to the pre-collapse
    // layout and overshoot into the next module.
    window.setTimeout(() => {
      const target = firstPromptId
        ? document.getElementById(`prompt-${firstPromptId}`)
        : document.querySelector<HTMLElement>(`[data-module-id="${moduleId}"]`);
      if (!target) return;
      // Compute the absolute Y manually, then scroll. More reliable than
      // scrollIntoView with smooth behavior, which can race with sticky
      // positioning calculations.
      const rect = target.getBoundingClientRect();
      const targetY = Math.max(0, window.scrollY + rect.top - 110);
      window.scrollTo({ top: targetY, behavior: 'smooth' });
      // Auto-expand the first prompt so the user lands on readable content,
      // not just the header. 500ms lets the smooth scroll finish first.
      window.setTimeout(() => {
        if (!firstPromptId) return;
        const el = document.getElementById(`prompt-${firstPromptId}`);
        if (!el || el.classList.contains('is-expanded')) return;
        el.querySelector<HTMLButtonElement>('.prompt-header')?.click();
      }, 550);
    }, 50);
  }

  return (
    <div className="next-up-body next-up-picker">
      <div className="next-up-picker-prompt">
        <div className="next-up-rank">Core Message complete</div>
        <div className="next-up-title">Pick the platform you use most</div>
        <div className="next-up-desc">
          Update your most active platform first. You'll see the message sharpen immediately.
        </div>
      </div>
      <div className="next-up-picker-grid">
        {platforms.map((m) => (
          <button
            key={m.module_id}
            type="button"
            className="next-up-platform"
            onClick={() => pickPlatform(m.module_id)}
          >
            <div className="next-up-platform-name">{cleanModuleTitle(m)}</div>
            <div className="next-up-platform-count">{m.prompt_ids.length} prompts</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared "next prompt" body (used by both modes)
// ─────────────────────────────────────────────────────────────────────────────

function NextPromptBody({
  promptLookup,
  promptId,
  rankLabel,
  isStuck,
  primaryCtaLabel,
  onPrimaryCta,
  secondaryLink,
}: {
  promptLookup: Map<string, Prompt>;
  promptId: string;
  rankLabel: string;
  isStuck: boolean;
  primaryCtaLabel: string;
  onPrimaryCta: () => void;
  secondaryLink?: { label: string; onClick: () => void };
}) {
  const prompt = promptLookup.get(promptId);
  return (
    <div className={`next-up-body${isStuck ? ' next-up-body-compact' : ''}`}>
      <div className="next-up-next">
        <div className="next-up-rank">{rankLabel}</div>
        <div className="next-up-title">{prompt?.title ?? 'Next prompt'}</div>
        {!isStuck && prompt?.what_it_does ? (
          <div className="next-up-desc">{prompt.what_it_does}</div>
        ) : null}
      </div>
      <div className="next-up-actions">
        <button type="button" className="next-up-cta" onClick={onPrimaryCta}>
          {primaryCtaLabel}
          <IconArrowRight aria-hidden="true" />
        </button>
        {secondaryLink && !isStuck ? (
          <button type="button" className="next-up-link" onClick={secondaryLink.onClick}>
            {secondaryLink.label}
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function scrollToPrompt(promptId: string) {
  const el = document.getElementById(`prompt-${promptId}`);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  window.setTimeout(() => {
    const header = el.querySelector<HTMLButtonElement>('.prompt-header');
    if (!el.classList.contains('is-expanded')) header?.click();
  }, 350);
}
